import { useCallback } from 'react';
import type { Map } from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { useMapStore } from '../stores/mapStore.js';
import { useDrawingStore, getGlobalDraw, setGlobalDraw } from '../stores/drawingStore.js';

export type DrawMode = 'draw_polygon' | 'draw_rectangle' | 'draw_circle' | 'simple_select';

// ─── Custom: Rectangle mode (2 clicks: start corner → end corner) ─────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DrawRectangle: any = {
  onSetup() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rect = (this as any).newFeature({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [[]] },
      properties: {},
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).addFeature(rect);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).clearSelectedFeatures();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).setActionableState({ trash: false, combineFeatures: false, uncombineFeatures: false });
    return { rect, start: null as [number, number] | null };
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick(state: any, e: any) {
    if (!state.start) {
      state.start = [e.lngLat.lng, e.lngLat.lat];
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this as any).map.fire('draw.create', { features: [state.rect.toGeoJSON()] });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this as any).changeMode('simple_select', { featureIds: [state.rect.id] });
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMouseMove(state: any, e: any) {
    if (!state.start) return;
    const [x1, y1] = state.start;
    const x2 = e.lngLat.lng, y2 = e.lngLat.lat;
    state.rect.updateCoordinate('0.0', x1, y1);
    state.rect.updateCoordinate('0.1', x2, y1);
    state.rect.updateCoordinate('0.2', x2, y2);
    state.rect.updateCoordinate('0.3', x1, y2);
    state.rect.updateCoordinate('0.4', x1, y1);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toDisplayFeatures(_state: any, geojson: any, display: (g: any) => void) {
    display(geojson);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onStop(state: any) {
    if ((state.rect.coordinates?.[0]?.length ?? 0) < 2) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this as any).deleteFeature([state.rect.id], { silent: true });
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyUp(_state: any, e: KeyboardEvent) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (e.key === 'Escape') (this as any).changeMode('simple_select');
  },
};

// ─── Custom: Circle mode (2 clicks: center → radius point) ────────────────────
const N_CIRCLE = 64;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DrawCircle: any = {
  onSetup() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const circle = (this as any).newFeature({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [[]] },
      properties: {},
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).addFeature(circle);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).clearSelectedFeatures();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).setActionableState({ trash: false, combineFeatures: false, uncombineFeatures: false });
    return { circle, center: null as [number, number] | null };
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick(state: any, e: any) {
    if (!state.center) {
      state.center = [e.lngLat.lng, e.lngLat.lat];
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this as any).map.fire('draw.create', { features: [state.circle.toGeoJSON()] });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this as any).changeMode('simple_select', { featureIds: [state.circle.id] });
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMouseMove(state: any, e: any) {
    if (!state.center) return;
    const [cx, cy] = state.center;
    const dx = e.lngLat.lng - cx;
    const dy = e.lngLat.lat - cy;
    const radius = Math.sqrt(dx * dx + dy * dy);
    if (radius === 0) return;
    for (let i = 0; i <= N_CIRCLE; i++) {
      const angle = (i / N_CIRCLE) * 2 * Math.PI;
      state.circle.updateCoordinate(`0.${i}`, cx + radius * Math.cos(angle), cy + radius * Math.sin(angle));
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toDisplayFeatures(_state: any, geojson: any, display: (g: any) => void) {
    display(geojson);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onStop(state: any) {
    if ((state.circle.coordinates?.[0]?.length ?? 0) < 3) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (this as any).deleteFeature([state.circle.id], { silent: true });
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyUp(_state: any, e: KeyboardEvent) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (e.key === 'Escape') (this as any).changeMode('simple_select');
  },
};

export function useDrawing() {
  const { mode, pendingFeatures, setMode, addFeatures, updateFeatures, removeFeatures, clearFeatures } = useDrawingStore();
  const queryClient = useQueryClient();
  const { selectedPinId, activeProject } = useMapStore();

  const initDraw = useCallback((map: Map) => {
    if (getGlobalDraw()) return; // already initialised — shared across all hook instances
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      modes: {
        ...MapboxDraw.modes,
        draw_rectangle: DrawRectangle,
        draw_circle: DrawCircle,
      },
    });
    map.addControl(draw);
    map.on('draw.create', (e: { features: GeoJSON.Feature[] }) => addFeatures(e.features));
    map.on('draw.update', (e: { features: GeoJSON.Feature[] }) => updateFeatures(e.features));
    map.on('draw.delete', (e: { features: GeoJSON.Feature[] }) => {
      removeFeatures(new Set(e.features.map(f => f.id as string)));
    });
    setGlobalDraw(draw);
  }, [addFeatures, updateFeatures, removeFeatures]);

  const setDrawMode = useCallback((m: DrawMode) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (getGlobalDraw() as any)?.changeMode(m);
    setMode(m);
  }, [setMode]);

  // Save as pin-level drawing (legacy) or project-level (map mode)
  const saveDrawing = useCallback(async (name: string, color = '#4361ee', opacity = 0.4) => {
    if (pendingFeatures.length === 0) return;
    const geojson = JSON.stringify({ type: 'FeatureCollection', features: pendingFeatures });

    let result;
    if (selectedPinId) {
      result = await api.post('/drawings', { pinId: selectedPinId, name, geojson, color, opacity });
      if (result.success) queryClient.invalidateQueries({ queryKey: ['drawings', selectedPinId] });
    } else if (activeProject) {
      result = await api.post('/drawings', { projectId: activeProject.id, name, geojson, color, opacity });
      if (result.success) queryClient.invalidateQueries({ queryKey: ['drawings', activeProject.id] });
    } else {
      return;
    }

    if (result?.success) {
      getGlobalDraw()?.deleteAll();
      clearFeatures();
    }
    return result;
  }, [selectedPinId, activeProject, pendingFeatures, queryClient, clearFeatures]);

  const clearAll = useCallback(() => {
    getGlobalDraw()?.deleteAll();
    clearFeatures();
  }, [clearFeatures]);

  return { mode, pendingFeatures, initDraw, setDrawMode, saveDrawing, clearAll };
}
